// Planilha → registros `{ cabeçalho: valor }` para a importação de leads
// frios. CSV/TSV/texto colado do Sheets ou Excel passam por papaparse
// (delimitador detectado); XLSX chega como matriz de células
// (read-excel-file, carregado sob demanda em src/lib/readSpreadsheet.ts).
// O mapeamento cabeçalho → coluna e a coerção de valores ficam no módulo
// compartilhado com a API/MCP (supabase/functions/_shared/dashboard/prospectFields.ts).
import Papa from "papaparse";

export interface SheetRecords {
  headers: string[];
  records: Record<string, unknown>[];
}

/** Cabeçalhos vazios viram "Coluna N"; repetidos ganham sufixo " (2)". */
export function uniqueHeaders(raw: readonly unknown[]): string[] {
  const seen = new Map<string, number>();
  return raw.map((value, index) => {
    const base =
      String(value ?? "")
        .replace(/^\uFEFF/, "")
        .trim() || `Coluna ${index + 1}`;
    const n = (seen.get(base) ?? 0) + 1;
    seen.set(base, n);
    return n === 1 ? base : `${base} (${n})`;
  });
}

function isEmptyRow(row: readonly unknown[]): boolean {
  return row.every((cell) => cell === null || cell === undefined || String(cell).trim() === "");
}

/** Matriz (primeira linha = cabeçalho) → registros. Linhas vazias são puladas. */
export function matrixToRecords(matrix: readonly (readonly unknown[])[]): SheetRecords {
  const firstRow = matrix.findIndex((row) => !isEmptyRow(row));
  if (firstRow === -1) return { headers: [], records: [] };
  const headers = uniqueHeaders(matrix[firstRow]);
  const records = matrix
    .slice(firstRow + 1)
    .filter((row) => !isEmptyRow(row))
    .map((row) => {
      const record: Record<string, unknown> = {};
      headers.forEach((header, i) => {
        const cell = row[i];
        record[header] = typeof cell === "string" ? cell.trim() : (cell ?? "");
      });
      return record;
    });
  return { headers, records };
}

/**
 * Texto delimitado → registros. Tab (colado do Sheets/Excel), `;` (CSV do
 * Excel pt-BR) ou `,` — o papaparse escolhe pelo conteúdo.
 */
export function parseDelimitedText(text: string): SheetRecords {
  const clean = text.replace(/^\uFEFF/, "");
  const result = Papa.parse<string[]>(clean, {
    delimitersToGuess: ["\t", ";", ",", "|"],
    skipEmptyLines: "greedy",
  });
  return matrixToRecords(result.data);
}

/** Divide em lotes de `size` (a RPC aceita até 500 linhas por chamada). */
export function chunk<T>(items: readonly T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size));
  return out;
}
